import { memo, useEffect, useRef, useState } from 'react';
import { WifiOff, Wifi } from 'lucide-react';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { useSocket } from '@/hooks/useSocket';

/**
 * Slim banner that appears below the topbar only when connectivity is GENUINELY lost.
 *
 * `navigator.onLine` alone gives false negatives (VPN/proxy/stale state can report offline
 * while the API + WebSocket are fully reachable), so we treat the app as online whenever the
 * realtime socket is connected OR the browser reports online. We also debounce the offline
 * state so brief socket reconnects don't flash the warning.
 */
export const OfflineBanner = memo(function OfflineBanner() {
  const browserOnline = useOnlineStatus();
  const { isConnected } = useSocket();
  const online = isConnected || browserOnline; // live socket = definitive proof we're online

  const [offline, setOffline] = useState(false);
  const [showReconnected, setShowReconnected] = useState(false);
  const wasOffline = useRef(false);

  useEffect(() => {
    if (online) {
      if (wasOffline.current) { setShowReconnected(true); wasOffline.current = false; }
      setOffline(false);
      return;
    }
    // Only declare offline once it persists — avoids flicker during reconnects / initial connect.
    const t = setTimeout(() => { setOffline(true); wasOffline.current = true; }, 4000);
    return () => clearTimeout(t);
  }, [online]);

  useEffect(() => {
    if (!showReconnected) return;
    const t = setTimeout(() => setShowReconnected(false), 3000);
    return () => clearTimeout(t);
  }, [showReconnected]);

  if (!offline && !showReconnected) return null;
  const isOnline = !offline;

  return (
    <div
      className={`flex items-center justify-center gap-2 px-4 py-1.5 text-xs font-medium transition-colors ${
        isOnline
          ? 'bg-success/10 text-success-700 dark:text-success-500'
          : 'bg-warning/10 text-warning-700 dark:text-warning'
      }`}
      role="status"
      aria-live="assertive"
    >
      {isOnline ? (
        <>
          <Wifi className="h-3.5 w-3.5" />
          Connection restored
        </>
      ) : (
        <>
          <WifiOff className="h-3.5 w-3.5" />
          You're offline — changes won't be saved until you reconnect
        </>
      )}
    </div>
  );
});
